import { Node, Edge } from "vis-network";
import { Neo4jNode, Neo4jEdge } from "@/service-hooks/use-get-graph-data";

interface NodeStyle {
  color: { 
    background: string; 
    border: string; 
    highlight?: { background: string; border: string };
    hover?: { background: string; border: string };
  };
  font: { 
    color: string; 
    size: number; 
    face: string;
    strokeWidth?: number;
    strokeColor?: string;
  };
  shape: string;
  baseSize: number; // Base size for level 0 (root)
  shadow?: boolean;
  borderWidth?: number;
  hierarchy: number; // Importance level (0 = root, higher = less important)
}

// Neo4j-style node configurations with correct business hierarchy:
// Level 0: Project (root)
// Level 1: Architecture, Design, Functional (direct children of Project)  
// Level 2: Persona (child of Functional), Documents (variable)
// Level 3: Outcomes (child of Persona)
// Level 4: Scenarios (child of Outcomes)
// Level 5: Steps (child of Scenarios)
// Level 6: Actions (child of Steps)
const NODE_STYLES: Record<string, NodeStyle> = {
  Project: {
    color: { 
      background: "#68CCF1", 
      border: "#4A9ECA",
      highlight: { background: "#7DD3F0", border: "#68CCF1" },
      hover: { background: "#7DD3F0", border: "#68CCF1" }
    },
    font: { color: "#ffffff", size: 14, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 60, // Largest - root level
    shadow: false,
    borderWidth: 4,
    hierarchy: 0, // Root level
  },
  Persona: {
    color: { 
      background: "#FFB3E6", 
      border: "#E694D1",
      highlight: { background: "#FFC2EA", border: "#FFB3E6" },
      hover: { background: "#FFC2EA", border: "#FFB3E6" }
    },
    font: { color: "#ffffff", size: 12, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 42, // Child of Functional
    shadow: false,
    borderWidth: 3,
    hierarchy: 2, // Persona is child of Functional
  },
  Outcome: {
    color: { 
      background: "#A4DD00", 
      border: "#8BC234",
      highlight: { background: "#B3E01A", border: "#A4DD00" },
      hover: { background: "#B3E01A", border: "#A4DD00" }
    },
    font: { color: "#ffffff", size: 12, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 36, // Child of Persona
    shadow: false,
    borderWidth: 3,
    hierarchy: 3, // Outcome is child of Persona
  },
  Action: {
    color: { 
      background: "#FB8500", 
      border: "#D96D00",
      highlight: { background: "#FF9419", border: "#FB8500" },
      hover: { background: "#FF9419", border: "#FB8500" }
    },
    font: { color: "#ffffff", size: 10, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 24, // Child of Steps - smallest
    shadow: false,
    borderWidth: 3,
    hierarchy: 6, // Action is child of Steps
  },
  Step: {
    color: { 
      background: "#AD7BE9", 
      border: "#9B6AD4",
      highlight: { background: "#B88AED", border: "#AD7BE9" },
      hover: { background: "#B88AED", border: "#AD7BE9" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 28, // Child of Scenarios
    shadow: false,
    borderWidth: 3,
    hierarchy: 5, // Step is child of Scenarios
  },
  Scenario: {
    color: { 
      background: "#569480", 
      border: "#4A7F6E",
      highlight: { background: "#62A389", border: "#569480" },
      hover: { background: "#62A389", border: "#569480" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 32, // Child of Outcomes
    shadow: false,
    borderWidth: 3,
    hierarchy: 4, // Scenario is child of Outcomes
  },
  Document: {
    color: { 
      background: "#F79767", 
      border: "#E8825A",
      highlight: { background: "#F9A577", border: "#F79767" },
      hover: { background: "#F9A577", border: "#F79767" }
    },
    font: { color: "#ffffff", size: 12, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38, // Variable level depending on context
    shadow: false,
    borderWidth: 3,
    hierarchy: 2, // Fallback level - can be at various levels
  },
  Architectural: {
    color: { 
      background: "#C990C0", 
      border: "#B67FAC",
      highlight: { background: "#D19FC9", border: "#C990C0" },
      hover: { background: "#D19FC9", border: "#C990C0" }
    },
    font: { color: "#ffffff", size: 13, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 50, // Direct child of Project
    shadow: false,
    borderWidth: 3,
    hierarchy: 1, // Direct child of Project
  },
  Functional: {
    color: { 
      background: "#F16667", 
      border: "#E85153",
      highlight: { background: "#F37577", border: "#F16667" },
      hover: { background: "#F37577", border: "#F16667" }
    },
    font: { color: "#ffffff", size: 13, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 50, // Direct child of Project
    shadow: false,
    borderWidth: 3,
    hierarchy: 1, // Direct child of Project
  },
  Design: {
    color: { 
      background: "#4C8EDA", 
      border: "#3A7BC8",
      highlight: { background: "#5C9EE2", border: "#4C8EDA" },
      hover: { background: "#5C9EE2", border: "#4C8EDA" }
    },
    font: { color: "#ffffff", size: 13, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 50, // Direct child of Project
    shadow: false,
    borderWidth: 3,
    hierarchy: 1, // Direct child of Project
  },
  // Architectural subcategories - children of Architectural node
  UserExperience: {
    color: {
      background: "#E6A3DC",
      border: "#D285C8",
      highlight: { background: "#EBB2E1", border: "#E6A3DC" },
      hover: { background: "#EBB2E1", border: "#E6A3DC" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  ApiGateway: {
    color: {
      background: "#D4A3E6",
      border: "#C085D2",
      highlight: { background: "#D9B2EB", border: "#D4A3E6" },
      hover: { background: "#D9B2EB", border: "#D4A3E6" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  ObservabilityMonitoring: {
    color: {
      background: "#B0A3E6",
      border: "#9C85D2",
      highlight: { background: "#B5B2EB", border: "#B0A3E6" },
      hover: { background: "#B5B2EB", border: "#B0A3E6" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  Agents: {
    color: {
      background: "#A3C2E6",
      border: "#85AED2",
      highlight: { background: "#B2D1EB", border: "#A3C2E6" },
      hover: { background: "#B2D1EB", border: "#A3C2E6" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  Services: {
    color: {
      background: "#A3E6C2",
      border: "#85D2AE",
      highlight: { background: "#B2EBD1", border: "#A3E6C2" },
      hover: { background: "#B2EBD1", border: "#A3E6C2" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  EventQueue: {
    color: {
      background: "#E6D4A3",
      border: "#D2C085",
      highlight: { background: "#EBD9B2", border: "#E6D4A3" },
      hover: { background: "#EBD9B2", border: "#E6D4A3" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  DataLake: {
    color: {
      background: "#A3D4E6",
      border: "#85C0D2",
      highlight: { background: "#B2D9EB", border: "#A3D4E6" },
      hover: { background: "#B2D9EB", border: "#A3D4E6" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },

  // DataLake is the one architecture label whose `type` property is a real
  // discriminator (Relational | Non-Relational | Elastic | Graph), so it gets a colour
  // per store kind instead of one flat blue. Resolved by resolveNodeStyle().
  "DataLake:Relational": {
    color: {
      background: "#3E7CB1",
      border: "#2F6491",
      highlight: { background: "#3E7CB1", border: "#2F6491" },
      hover: { background: "#3E7CB1", border: "#2F6491" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  "DataLake:Non-Relational": {
    color: {
      background: "#5B8C5A",
      border: "#456B45",
      highlight: { background: "#5B8C5A", border: "#456B45" },
      hover: { background: "#5B8C5A", border: "#456B45" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  "DataLake:Graph": {
    color: {
      background: "#7A5BC9",
      border: "#5E44A3",
      highlight: { background: "#7A5BC9", border: "#5E44A3" },
      hover: { background: "#7A5BC9", border: "#5E44A3" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  "DataLake:Elastic": {
    color: {
      background: "#C98A3C",
      border: "#A76F2C",
      highlight: { background: "#C98A3C", border: "#A76F2C" },
      hover: { background: "#C98A3C", border: "#A76F2C" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  Infrastructure: {
    color: {
      background: "#E6B0A3",
      border: "#D29C85",
      highlight: { background: "#EBB5B2", border: "#E6B0A3" },
      hover: { background: "#EBB5B2", border: "#E6B0A3" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  // --- Code ontology, reached through the synthetic Code/Repository nodes.
  // Shape carries the distinction as well as colour, so the levels stay apart
  // when several are on screen — and for anyone who cannot separate the hues.
  //
  // Every shape here draws its label OUTSIDE the node. vis-network's box,
  // ellipse, circle and database shapes draw it inside and ignore baseSize, so
  // they neither match the surrounding nodes nor shrink with depth.
  //
  // There is deliberately no `Method` entry: methods are not their own label.
  // code-containment.util.ts states the rule — "HAS_METHOD: function.parentId =
  // Class id" — so a method IS a Function node, distinguished only by arriving
  // on a HAS_METHOD edge. That distinction is carried in EDGE_STYLES below.
  Code: {
    color: {
      background: "#6C5CE7",
      border: "#5646C4",
      highlight: { background: "#6C5CE7", border: "#5646C4" },
      hover: { background: "#6C5CE7", border: "#5646C4" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 50,
    shadow: false,
    borderWidth: 2,
    hierarchy: 1,
  },
  Repository: {
    color: {
      background: "#00A8A8",
      border: "#008B8B",
      highlight: { background: "#00A8A8", border: "#008B8B" },
      hover: { background: "#00A8A8", border: "#008B8B" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 40,
    shadow: false,
    borderWidth: 2,
    hierarchy: 2,
  },
  File: {
    color: {
      background: "#2E86AB",
      border: "#246E8C",
      highlight: { background: "#2E86AB", border: "#246E8C" },
      hover: { background: "#2E86AB", border: "#246E8C" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 34,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  Class: {
    color: {
      background: "#E8A33D",
      border: "#C9862A",
      highlight: { background: "#E8A33D", border: "#C9862A" },
      hover: { background: "#E8A33D", border: "#C9862A" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 30,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  Function: {
    color: {
      background: "#D65DB1",
      border: "#B44795",
      highlight: { background: "#D65DB1", border: "#B44795" },
      hover: { background: "#D65DB1", border: "#B44795" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 28,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  Statement: {
    color: {
      background: "#7F8C9B",
      border: "#66727F",
      highlight: { background: "#7F8C9B", border: "#66727F" },
      hover: { background: "#7F8C9B", border: "#66727F" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 20,
    shadow: false,
    borderWidth: 2,
    hierarchy: 6,
  },

  // --- Functional leaf, below Action
  Api: {
    color: {
      background: "#F4A261",
      border: "#D6844A",
      highlight: { background: "#F4A261", border: "#D6844A" },
      hover: { background: "#F4A261", border: "#D6844A" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 26,
    shadow: false,
    borderWidth: 2,
    hierarchy: 7,
  },

  // --- Design chain, reached from Scenario/Step via MAPS_TO
  UserJourney: {
    color: {
      background: "#6BA3E0",
      border: "#548BC4",
      highlight: { background: "#6BA3E0", border: "#548BC4" },
      hover: { background: "#6BA3E0", border: "#548BC4" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 38,
    shadow: false,
    borderWidth: 2,
    hierarchy: 5,
  },
  Flow: {
    color: {
      background: "#8CC7E8",
      border: "#6FAACB",
      highlight: { background: "#8CC7E8", border: "#6FAACB" },
      hover: { background: "#8CC7E8", border: "#6FAACB" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 32,
    shadow: false,
    borderWidth: 2,
    hierarchy: 6,
  },
  Page: {
    color: {
      background: "#A8D8EA",
      border: "#8ABBCD",
      highlight: { background: "#A8D8EA", border: "#8ABBCD" },
      hover: { background: "#A8D8EA", border: "#8ABBCD" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 28,
    shadow: false,
    borderWidth: 2,
    hierarchy: 7,
  },
  Component: {
    color: {
      background: "#7EC8B0",
      border: "#63AB94",
      highlight: { background: "#7EC8B0", border: "#63AB94" },
      hover: { background: "#7EC8B0", border: "#63AB94" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 24,
    shadow: false,
    borderWidth: 2,
    hierarchy: 8,
  },

  // --- Relational schema, under a DataLake
  DDLTable: {
    color: {
      background: "#2D9C5A",
      border: "#227A46",
      highlight: { background: "#2D9C5A", border: "#227A46" },
      hover: { background: "#2D9C5A", border: "#227A46" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 32,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  DDLColumn: {
    color: {
      background: "#7FD1A0",
      border: "#63B383",
      highlight: { background: "#7FD1A0", border: "#63B383" },
      hover: { background: "#7FD1A0", border: "#63B383" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 20,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  DDLConstraint: {
    color: {
      background: "#C8A415",
      border: "#A88A10",
      highlight: { background: "#C8A415", border: "#A88A10" },
      hover: { background: "#C8A415", border: "#A88A10" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 22,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  DDLIndex: {
    color: {
      background: "#5AA9C9",
      border: "#458AA8",
      highlight: { background: "#5AA9C9", border: "#458AA8" },
      hover: { background: "#5AA9C9", border: "#458AA8" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 22,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  DDLView: {
    color: {
      background: "#4FB3A5",
      border: "#3C9488",
      highlight: { background: "#4FB3A5", border: "#3C9488" },
      hover: { background: "#4FB3A5", border: "#3C9488" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 30,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  DDLProcedure: {
    color: {
      background: "#9B7EDE",
      border: "#7F63BD",
      highlight: { background: "#9B7EDE", border: "#7F63BD" },
      hover: { background: "#9B7EDE", border: "#7F63BD" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 30,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  DDLSequence: {
    color: {
      background: "#C98A5A",
      border: "#A96E44",
      highlight: { background: "#C98A5A", border: "#A96E44" },
      hover: { background: "#C98A5A", border: "#A96E44" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 26,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },

  // --- Elasticsearch schema
  ESIndex: {
    color: {
      background: "#E8734A",
      border: "#C55937",
      highlight: { background: "#E8734A", border: "#C55937" },
      hover: { background: "#E8734A", border: "#C55937" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 32,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  ESField: {
    color: {
      background: "#F2A585",
      border: "#CE8568",
      highlight: { background: "#F2A585", border: "#CE8568" },
      hover: { background: "#F2A585", border: "#CE8568" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 20,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  ESAlias: {
    color: {
      background: "#C95D9B",
      border: "#A6477E",
      highlight: { background: "#C95D9B", border: "#A6477E" },
      hover: { background: "#C95D9B", border: "#A6477E" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 24,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },

  // --- NoSQL schema
  NoSQLCollection: {
    color: {
      background: "#3B9C8F",
      border: "#2D7A70",
      highlight: { background: "#3B9C8F", border: "#2D7A70" },
      hover: { background: "#3B9C8F", border: "#2D7A70" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 32,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  NoSQLField: {
    color: {
      background: "#8FD4C9",
      border: "#70B3A8",
      highlight: { background: "#8FD4C9", border: "#70B3A8" },
      hover: { background: "#8FD4C9", border: "#70B3A8" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 20,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },
  NoSQLIndex: {
    color: {
      background: "#5A8FC9",
      border: "#4573A8",
      highlight: { background: "#5A8FC9", border: "#4573A8" },
      hover: { background: "#5A8FC9", border: "#4573A8" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 22,
    shadow: false,
    borderWidth: 2,
    hierarchy: 4,
  },

  // --- Graph database schema (flat under a DataLake)
  GraphNodeType: {
    color: {
      background: "#7A5BC9",
      border: "#5E44A3",
      highlight: { background: "#7A5BC9", border: "#5E44A3" },
      hover: { background: "#7A5BC9", border: "#5E44A3" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "square",
    baseSize: 32,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  GraphEdgeType: {
    color: {
      background: "#C9788F",
      border: "#A35C70",
      highlight: { background: "#C9788F", border: "#A35C70" },
      hover: { background: "#C9788F", border: "#A35C70" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 24,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
  GraphIndex: {
    color: {
      background: "#C9A24B",
      border: "#A3823A",
      highlight: { background: "#C9A24B", border: "#A3823A" },
      hover: { background: "#C9A24B", border: "#A3823A" }
    },
    font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
    shape: "dot",
    baseSize: 22,
    shadow: false,
    borderWidth: 2,
    hierarchy: 3,
  },
};

/**
 * Legend, derived from NODE_STYLES rather than restated.
 *
 * The legend used to be a hand-copied duplicate of the colours below, which meant
 * it could disagree with what was actually drawn and had to be edited twice for
 * every new label. Reading NODE_STYLES makes that impossible, and any label added
 * there shows up in the legend automatically.
 */
export type LegendGroup = 'Functional' | 'Design' | 'Code' | 'Architecture' | 'Data';

const LEGEND_GROUPS: Record<LegendGroup, string[]> = {
  Functional: ['Project', 'Functional', 'Persona', 'Outcome', 'Scenario', 'Step', 'Action', 'Api', 'Document'],
  Design: ['Design', 'UserJourney', 'Flow', 'Page', 'Component'],
  Code: ['Code', 'Repository', 'File', 'Class', 'Function', 'Statement'],
  Architecture: [
    'Architectural', 'UserExperience', 'ApiGateway', 'Services', 'Agents',
    'EventQueue', 'DataLake', 'ObservabilityMonitoring', 'Infrastructure',
  ],
  Data: [
    'DDLTable', 'DDLColumn', 'DDLConstraint', 'DDLIndex', 'DDLView', 'DDLProcedure', 'DDLSequence',
    'ESIndex', 'ESField', 'ESAlias',
    'NoSQLCollection', 'NoSQLField', 'NoSQLIndex',
    'GraphNodeType', 'GraphEdgeType', 'GraphIndex',
  ],
};

const LEGEND_DESCRIPTIONS: Record<string, string> = {
  Project: 'Root project node',
  Functional: 'Functional ontology root',
  Persona: 'Who the system serves',
  Outcome: 'What a persona wants to achieve',
  Scenario: 'A path to an outcome — also where design attaches',
  Step: 'A step within a scenario',
  Action: 'A concrete action in a step',
  Api: 'Endpoint an action calls',
  Document: 'Source document',
  Design: 'Design ontology root',
  UserJourney: 'Reached from Scenario via MAPS_TO',
  Flow: 'Flow within a journey',
  Page: 'Page within a flow',
  Component: 'UI component; may nest',
  Code: 'Repositories (PostgreSQL, not a graph node)',
  Repository: 'One onboarded repository',
  File: 'Source file',
  Class: 'Class declaration',
  Function: 'Function, or a class method via HAS_METHOD',
  Statement: 'Statement inside a file, class or function',
  Architectural: 'Architecture ontology root',
  DataLake: 'A datastore — relational, search or document',
  DDLTable: 'Relational table',
  DDLColumn: 'Table column',
  DDLConstraint: 'Table constraint',
  DDLIndex: 'Table index',
  DDLView: 'Database view',
  DDLProcedure: 'Stored procedure',
  DDLSequence: 'Sequence',
  ESIndex: 'Elasticsearch index',
  ESField: 'Field; nests via HAS_SUB_FIELD',
  ESAlias: 'Index alias',
  NoSQLCollection: 'Document collection',
  NoSQLField: 'Field; nests via HAS_SUB_FIELD',
  NoSQLIndex: 'Collection index',
  GraphNodeType: 'Graph database node type',
  GraphEdgeType: 'Graph database edge type; names its two node types',
  GraphIndex: 'Graph database index',
};

export interface LegendEntry {
  type: string;
  color: string;
  border: string;
  shape: string;
  description: string;
  hierarchy: number;
}

export function getLegendGroups(): { group: LegendGroup; entries: LegendEntry[] }[] {
  return (Object.keys(LEGEND_GROUPS) as LegendGroup[]).map((group) => ({
    group,
    entries: LEGEND_GROUPS[group]
      .filter((label) => NODE_STYLES[label])
      .map((label) => {
        const style = NODE_STYLES[label];
        return {
          type: label,
          color: style.color.background,
          border: style.color.border,
          shape: style.shape,
          description: LEGEND_DESCRIPTIONS[label] ?? label,
          hierarchy: style.hierarchy,
        };
      })
      .sort((a, b) => a.hierarchy - b.hierarchy || a.type.localeCompare(b.type)),
  }));
}

// Professional edge style configurations with modern colors
const EDGE_STYLES: Record<string, { color: string; label: string; width?: number; dashes?: boolean }> = {
  CONTAINS: {
    color: "#6b7280",
    label: "contains",
    width: 2,
  },
  DEPENDS_ON: {
    color: "#3b82f6",
    label: "depends on",
    width: 3,
  },
  IMPLEMENTS: {
    color: "#10b981",
    label: "implements",
    width: 3,
  },
  REFERENCES: {
    color: "#f59e0b",
    label: "references",
    width: 2,
  },
  BELONGS_TO: {
    color: "#8b5cf6",
    label: "belongs to",
    width: 2,
  },

  // --- Code ontology containment. Solid and muted: these are the drill-down
  // path, so they should recede behind the nodes rather than compete with them.
  HAS_CLASS:     { color: "#94a3b8", label: "class",     width: 2 },
  HAS_FUNCTION:  { color: "#94a3b8", label: "function",  width: 2 },
  HAS_METHOD:    { color: "#845EC2", label: "method",    width: 2 },
  HAS_STATEMENT: { color: "#cbd5e1", label: "statement", width: 1 },

  // --- Cross-references, NOT containment. Dashed, because expanding a File also
  // pulls in the files it imports and expanding a Function pulls in what it
  // calls — useful, but the user has to be able to see it is not the hierarchy.
  IMPORTS:  { color: "#0ea5e9", label: "imports",  width: 1, dashes: true },
  CALLS:    { color: "#ec4899", label: "calls",    width: 1, dashes: true },
  EXTENDS:  { color: "#f59e0b", label: "extends",  width: 2, dashes: true },

  // --- The functional <-> design join, and functional -> code.
  MAPS_TO:        { color: "#7C3AED", label: "maps to",        width: 2, dashes: true },
  IMPLEMENTED_BY: { color: "#0d9488", label: "implemented by", width: 2, dashes: true },

  // --- Design ontology containment. Named, because these edge types are not
  // guessable from the node labels the way the HAS_* ones are.
  // Synthesised by the UI: Design does not own its journeys in the graph.
  HAS_USER_JOURNEY: { color: "#6BA3E0", label: "user journey", width: 2 },
  INCLUDES_FLOW: { color: "#6BA3E0", label: "includes flow", width: 2 },
  CONTAINS_PAGE: { color: "#8CC7E8", label: "contains page", width: 2 },

  // --- Design source material, under the Design root.
  HAS_WEBSITE:  { color: "#3C7A89", label: "website",  width: 2 },
  HAS_SCREEN:   { color: "#5FA8B8", label: "screen",   width: 2 },
  HAS_RESOURCE: { color: "#9CCBD6", label: "resource", width: 1 },
};

// Default Neo4j-style node for unknown labels
const DEFAULT_NODE_STYLE: NodeStyle = {
  color: { 
    background: "#848484", 
    border: "#6F6F6F",
    highlight: { background: "#939393", border: "#848484" },
    hover: { background: "#939393", border: "#848484" }
  },
  font: { color: "#ffffff", size: 11, face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", strokeWidth: 0 },
  shape: "dot",
  baseSize: 30,
  shadow: false,
  borderWidth: 3,
  hierarchy: 4,
};

// Default professional edge style for unknown relationship types
const DEFAULT_EDGE_STYLE = {
  color: "#6b7280",
  label: "",
  width: 2,
};

/**
 * Style for a node. Label alone is usually enough, but DataLake carries a
 * `type` (Relational | Non-Relational | Elastic | Graph) that means genuinely different
 * infrastructure, so it resolves to a per-type variant when one exists.
 * Falls back to the base label style, then to DEFAULT_NODE_STYLE.
 */
export function resolveNodeStyle(
  primaryLabel: string,
  properties?: Record<string, any>
): NodeStyle {
  const subtype = primaryLabel === "DataLake" ? properties?.type : undefined;
  if (subtype && NODE_STYLES[`${primaryLabel}:${subtype}`]) {
    return NODE_STYLES[`${primaryLabel}:${subtype}`];
  }
  return NODE_STYLES[primaryLabel] || DEFAULT_NODE_STYLE;
}

export function transformNeo4jNodeToVisNode(neo4jNode: Neo4jNode, depth: number = 0, isLightTheme: boolean = false): Node {
  // Get primary label (first one) for styling
  const primaryLabel = neo4jNode.labels[0] || "";
  const style = resolveNodeStyle(primaryLabel, neo4jNode.properties);
  
  // Determine label text based on node properties
  let labelText = "";
  if (neo4jNode.properties.name) {
    labelText = neo4jNode.properties.name;
  } else if (neo4jNode.properties.persona) {
    labelText = neo4jNode.properties.persona;
  } else if (neo4jNode.properties.outcome) {
    labelText = neo4jNode.properties.outcome;
  } else if (neo4jNode.properties.action) {
    labelText = neo4jNode.properties.action;
  } else if (neo4jNode.properties.step) {
    labelText = neo4jNode.properties.step;
  } else if (neo4jNode.properties.scenario) {
    labelText = neo4jNode.properties.scenario;
  } else {
    labelText = primaryLabel || "Unknown";
  }

  // Colour alone is a poor encoder — spell the store kind out on the node too.
  if (primaryLabel === "DataLake" && neo4jNode.properties.type) {
    labelText = `${labelText}\n(${neo4jNode.properties.type})`;
  }

  // Create title for hover tooltip
  const properties = Object.entries(neo4jNode.properties)
    .map(([key, value]) => `${key}: ${value}`)
    .join("\n");
  
  const title = `Labels: ${neo4jNode.labels.join(", ")}\n${properties}`;

  // Calculate size based on depth - each level is 15% smaller
  const sizeReduction = Math.pow(0.85, depth);
  const calculatedSize = Math.max(style.baseSize * sizeReduction, 20); // Minimum size of 20
  
  // Adjust font size based on depth
  const fontSizeReduction = Math.pow(0.9, depth);
  const calculatedFontSize = Math.max(style.font.size * fontSizeReduction, 9); // Minimum font size of 9

  return {
    id: neo4jNode.id,
    label: labelText,
    title: `${title}\nDepth: ${depth}`,
    group: primaryLabel.toLowerCase(),
    color: style.color,
    font: {
      ...style.font,
      size: calculatedFontSize,
      color: isLightTheme ? "#0F172A" : "#FFFFFF",
      // Halo in the canvas colour. Without it a label that happens to overlap a
      // node or an edge is unreadable, and the per-label colours in NODE_STYLES
      // are the node fill, not something that has to work as text.
      strokeWidth: 3,
      strokeColor: isLightTheme ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.88)",
    },
    shape: style.shape,
    size: calculatedSize,
    shadow: style.shadow,
    borderWidth: style.borderWidth,
    chosen: {
      node: (values: any, _id: any, selected: boolean, hovering: boolean) => {
        try {
          if (selected) {
            values.borderWidth = 6;
            values.borderColor = "#FFD93D";
            if (values.color && typeof values.color === 'object') {
              values.color.border = "#FFD93D";
            }
          } else if (hovering) {
            values.borderWidth = style.borderWidth ? style.borderWidth + 1 : 4;
          }
        } catch (error) {
          console.error('Error in node chosen callback:', error);
        }
      },
      label: (values: any, _id: any, selected: boolean, hovering: boolean) => {
        try {
          if (selected || hovering) {
            values.size = calculatedFontSize + 1;
            // Was hardcoded #ffffff, which made every label invisible against the
            // light canvas the moment it was hovered or clicked.
            values.color = isLightTheme ? "#0F172A" : "#FFFFFF";
          }
        } catch (error) {
          console.error('Error in label chosen callback:', error);
        }
      },
    },
    scaling: {
      min: 15,
      max: 80,
      label: {
        enabled: true,
        min: 10,
        max: 16,
        maxVisible: 30,
        drawThreshold: 1,
      },
    },
  };
}

export function transformNeo4jEdgeToVisEdge(neo4jEdge: Neo4jEdge): Edge {
  const edgeStyle = EDGE_STYLES[neo4jEdge.type] || DEFAULT_EDGE_STYLE;
  
  // Create title for hover tooltip
  const properties = Object.entries(neo4jEdge.properties)
    .map(([key, value]) => `${key}: ${value}`)
    .join("\n");
  
  const title = `Type: ${neo4jEdge.type}\n${properties}`;

  return {
    id: neo4jEdge.id,
    from: neo4jEdge.from,
    to: neo4jEdge.to,
    label: edgeStyle.label,
    title,
    color: { 
      color: edgeStyle.color, 
      highlight: "#FFD93D",
      hover: edgeStyle.color,
      opacity: 0.7
    },
    width: edgeStyle.width || 2,
    dashes: edgeStyle.dashes ?? false,
    arrows: { 
      to: { 
        enabled: true, 
        scaleFactor: 1,
        type: "arrow"
      } 
    },
    font: {
      color: "#333333",
      size: 11,
      face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: "rgba(255,255,255,0.8)",
      strokeWidth: 0,
      align: "middle",
    },
    smooth: {
      enabled: true,
      type: "straightCross",
      roundness: 0.1,
    },
    shadow: false,
  };
}

// Calculate node depths based on graph structure
function calculateNodeDepths(nodes: Neo4jNode[], edges: Neo4jEdge[]): Map<string, number> {
  const depths = new Map<string, number>();
  const adjacencyList = new Map<string, string[]>();
  
  // Build adjacency list
  edges.forEach(edge => {
    if (!adjacencyList.has(edge.from)) {
      adjacencyList.set(edge.from, []);
    }
    adjacencyList.get(edge.from)!.push(edge.to);
  });
  
  // Find Project nodes (root level)
  const projectNodes = nodes.filter(node => node.labels.includes('Project'));
  
  // BFS to calculate depths
  const queue: Array<{ nodeId: string; depth: number }> = [];
  const visited = new Set<string>();
  
  // Start with Project nodes at depth 0
  projectNodes.forEach(node => {
    depths.set(node.id, 0);
    queue.push({ nodeId: node.id, depth: 0 });
    visited.add(node.id);
  });
  
  while (queue.length > 0) {
    const { nodeId, depth } = queue.shift()!;
    const children = adjacencyList.get(nodeId) || [];
    
    children.forEach(childId => {
      if (!visited.has(childId)) {
        const newDepth = depth + 1;
        depths.set(childId, newDepth);
        queue.push({ nodeId: childId, depth: newDepth });
        visited.add(childId);
      }
    });
  }
  
  // Set depth for any unconnected nodes based on their hierarchy level
  nodes.forEach(node => {
    if (!depths.has(node.id)) {
      const primaryLabel = node.labels[0] || '';
      const style = resolveNodeStyle(primaryLabel, (node as any).properties);
      depths.set(node.id, style.hierarchy);
    }
  });
  
  return depths;
}

export function transformGraphData(neo4jData: { nodes: Neo4jNode[]; edges: Neo4jEdge[] }, isLightTheme: boolean = false) {
  const nodeDepths = calculateNodeDepths(neo4jData.nodes, neo4jData.edges);

  const visNodes = neo4jData.nodes.map(node => {
    const depth = nodeDepths.get(node.id) || 0;
    return transformNeo4jNodeToVisNode(node, depth, isLightTheme);
  });

  const visEdges = neo4jData.edges.map(transformNeo4jEdgeToVisEdge);

  return {
    nodes: visNodes,
    edges: visEdges,
  };
}